import { toASCII, toUnicode } from 'punycode/';

/**
 * Converts a unicode IDN domain to punycode (ASCII-compatible encoding).
 * Returns the original input on failure.
 */
export const toPunycode = (domain: string): string => {
  try {
    return toASCII(domain);
  } catch {
    return domain;
  }
};

/**
 * Converts a punycode domain to unicode (human-readable form).
 * Returns the original input on failure.
 */
export const fromPunycode = (domain: string): string => {
  try {
    return toUnicode(domain);
  } catch {
    return domain;
  }
};

/**
 * Checks if a domain contains punycode-encoded labels (xn-- prefix).
 */
export const isPunycode = (domain: string): boolean => {
  return domain.split('.').some(label => label.startsWith('xn--'));
};

/**
 * Returns the unicode display form of a threat name if it's punycode,
 * otherwise returns it unchanged.
 */
export const getDisplayThreatName = (name: string): string => {
  if (!name) return name;
  if (isPunycode(name)) {
    return fromPunycode(name);
  }
  return name;
};

/**
 * Returns the full threat URL for a threat list row. The API's `threat_name`
 * is only the bare host; the full URL (with scheme and path) lives in
 * `value.value`. Falls back to `threat_name` when the indicator value is
 * missing.
 */
export const getThreatUrl = (threat: {
  threat_name?: string;
  value?: { value?: string };
}): string => threat?.value?.value || threat?.threat_name || '';

/**
 * Defangs a URL by replacing protocols and dots to prevent accidental clicks.
 * Handles http/https → hxxp/hxxps and dots → [.]
 * This is the logical inverse of refangDomain.
 */
export const defangUrl = (url: string): string => {
  return url
    .replace(/^https/i, 'hxxps')
    .replace(/^http/i, 'hxxp')
    .replace(/\./g, '[.]');
};

/**
 * Helper function to refang (un-defang) domains and clean URLs
 * Converts defanged domain formats back to regular format and removes protocols/paths
 *
 * Handles multiple defang styles:
 * - Dots: [.], (.), {.}
 * - At symbols: [@], (@), {@}
 * - Obfuscated protocols: hxxp://, hxxps://
 *
 * @param domain - The domain or URL string to refang
 * @returns Cleaned domain without protocols, paths, or port numbers
 */
export const refangDomain = (domain: string): string => {
  let cleaned = domain
    .replace(/\[\.]/g, '.') // Replace [.] with .
    .replace(/\(\.\)/g, '.') // Replace (.) with .
    .replace(/\{\.\}/g, '.') // Replace {.} with .
    .replace(/\[@\]/g, '@') // Replace [@] with @
    .replace(/\(@\)/g, '@') // Replace (@) with @
    .replace(/\{@\}/g, '@') // Replace {@} with @
    .trim();

  // Remove protocols (http://, https://, hxxp://, hxxps://)
  cleaned = cleaned
    .replace(/^hxxps?:\/\//gi, '') // Remove hxxp:// or hxxps://
    .replace(/^https?:\/\//gi, '') // Remove http:// or https://
    .replace(/^:\/\//, ''); // Remove any remaining ://

  // Remove trailing slashes and paths - extract just the domain
  cleaned = cleaned.split('/')[0];

  // Remove port numbers if present
  cleaned = cleaned.split(':')[0];

  return cleaned;
};
